import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { FaTimes, FaSpinner } from 'react-icons/fa';
import styles from './AmazonPushModal.module.css';

// Backdrop component
const Backdrop = (props) => {
  return <div className={styles.backdrop} onClick={props.onClose} />;
};

// Single selectable image tile. Hides itself if the image URL fails to load
// (e.g. an existing Amazon A+ image whose URL is not publicly resolvable).
const ImageTile = ({ item, selected, disabled, onToggle }) => {
  const [failed, setFailed] = useState(false);
  if (failed) return null;
  return (
    <button
      type="button"
      className={`${styles.imageTile} ${selected ? styles.imageTileSelected : ''} ${disabled ? styles.imageTileDisabled : ''}`}
      onClick={() => onToggle(item.url)}
      disabled={disabled}
      aria-pressed={selected}
    >
      <img
        src={item.url}
        alt=""
        className={styles.imageTileImg}
        loading="lazy"
        onError={() => setFailed(true)}
      />
      <span className={`${styles.imageCheck} ${selected ? styles.imageCheckOn : ''}`} />
    </button>
  );
};

// Modal overlay component
const ModalOverlay = (props) => {
  const { galleryImages, aplusImages, maxGallery, loadingExisting, isPushing, onConfirm, onClose } = props;

  // Nothing is pre-selected: the user explicitly picks which images to push.
  const [selectedGallery, setSelectedGallery] = useState(() => new Set());
  const [selectedAplus, setSelectedAplus] = useState(() => new Set());

  const galleryLimitReached = selectedGallery.size >= maxGallery;

  const toggleGallery = (url) => {
    setSelectedGallery(prev => {
      const next = new Set(prev);
      if (next.has(url)) {
        next.delete(url);
      } else if (next.size < maxGallery) {
        next.add(url);
      }
      return next;
    });
  };

  const toggleAplus = (url) => {
    setSelectedAplus(prev => {
      const next = new Set(prev);
      if (next.has(url)) next.delete(url); else next.add(url);
      return next;
    });
  };

  const totalSelected = selectedGallery.size + selectedAplus.size;

  const handleConfirm = () => {
    onConfirm(Array.from(selectedGallery), Array.from(selectedAplus));
  };

  const generatedGallery = galleryImages.filter(img => img.source === 'generated');
  const existingGallery = galleryImages.filter(img => img.source === 'existing');
  const generatedAplus = aplusImages.filter(img => img.source === 'generated');
  const existingAplus = aplusImages.filter(img => img.source === 'existing');

  const renderGalleryTile = (item) => {
    const selected = selectedGallery.has(item.url);
    return (
      <ImageTile
        key={item.url}
        item={item}
        selected={selected}
        disabled={!selected && galleryLimitReached}
        onToggle={toggleGallery}
      />
    );
  };

  const renderAplusTile = (item) => (
    <ImageTile
      key={item.url}
      item={item}
      selected={selectedAplus.has(item.url)}
      disabled={false}
      onToggle={toggleAplus}
    />
  );

  return (
    <div className={styles.modal} style={{ maxWidth: '820px', width: '92%' }}>
      <header className={styles.header}>
        <h2>Select images to push</h2>
        <button onClick={onClose} className={styles.closeButton} aria-label="Close Modal">
          <FaTimes />
        </button>
      </header>

      <div className={`${styles.content} ${styles.selectionContent}`}>
        {/* Selection summary */}
        <div className={styles.sectionHeaderRow}>
          <span className={`${styles.countPill} ${galleryLimitReached ? styles.countPillFull : ''}`}>
            Gallery: {selectedGallery.size} / {maxGallery}
          </span>
          {aplusImages.length > 0 && (
            <span className={styles.countPill}>A+: {selectedAplus.size}</span>
          )}
        </div>

        <div className={styles.columns}>
          {/* Left: Generated */}
          <div className={styles.column}>
            <h3 className={styles.sectionTitle}>Generated</h3>
            {generatedGallery.length > 0 ? (
              <div className={styles.imageGrid}>
                {generatedGallery.map(renderGalleryTile)}
              </div>
            ) : (
              <p className={styles.emptyNote}>No generated gallery images.</p>
            )}

            {generatedAplus.length > 0 && (
              <>
                <h4 className={styles.subTitle}>A+ content</h4>
                <div className={styles.imageGrid}>
                  {generatedAplus.map(renderAplusTile)}
                </div>
              </>
            )}
          </div>

          <div className={styles.columnDivider} />

          {/* Right: On Amazon */}
          <div className={styles.column}>
            <h3 className={styles.sectionTitle}>On Amazon</h3>
            {loadingExisting ? (
              <p className={styles.loadingNote}>
                <FaSpinner className={styles.spin} /> Loading existing Amazon images…
              </p>
            ) : existingGallery.length > 0 ? (
              <div className={styles.imageGrid}>
                {existingGallery.map(renderGalleryTile)}
              </div>
            ) : (
              <p className={styles.emptyNote}>No existing Amazon images.</p>
            )}

            {!loadingExisting && existingAplus.length > 0 && (
              <>
                <h4 className={styles.subTitle}>A+ content</h4>
                <div className={styles.imageGrid}>
                  {existingAplus.map(renderAplusTile)}
                </div>
              </>
            )}
          </div>
        </div>

        <div className={styles.selectionFooter}>
          <button onClick={onClose} className={styles.selectionCancel} disabled={isPushing}>
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            className={styles.yesButton}
            disabled={isPushing || totalSelected === 0}
          >
            {isPushing ? <FaSpinner className={styles.spin} /> : `Continue (${totalSelected})`}
          </button>
        </div>
      </div>
    </div>
  );
};

// Main Modal component using React Portals
const AmazonImageSelectionModal = (props) => {
  if (typeof window === 'undefined') return null;

  const portalElement = document.getElementById('modal-root');
  if (!portalElement) return null;
  if (!props.show) return null;

  return (
    <React.Fragment>
      {ReactDOM.createPortal(
        <Backdrop onClose={props.onClose} />,
        portalElement
      )}
      {ReactDOM.createPortal(
        <ModalOverlay {...props} />,
        portalElement
      )}
    </React.Fragment>
  );
};

export default AmazonImageSelectionModal;
